import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { TextareaInput } from "@/components/ui/Field";
import { NativeSelectField } from "@/components/ui/NativeSelect";
import { TimesheetBillingNotesPanel } from "@/features/timesheetFlow/components/TimesheetBillingNotesPanel";
import { TimesheetFlowCard } from "@/features/timesheetFlow/components/TimesheetFlowCard";
import type { TimesheetPermissions } from "@/features/timesheetFlow/timesheetPermissions";
import {
  formatBillingMaterialLine,
  formatHoursTotal,
  showGdInvoiceLinkInBillingTab,
} from "@/features/timesheetFlow/timesheetBillingUtils";
import {
  assignBilling,
  createBillingQuestions,
  getActionLog,
  gdProjectInvoiceOptionLabel,
  heroInvoiceOptionLabel,
  listBillingQuestions,
  listGdProjectInvoices,
  listHeroProjectInvoices,
  type ActionLogEntry,
  type BillingQuestion,
  type GdProjectInvoiceOption,
  type HeroProjectInvoiceOption,
} from "@/features/timesheetFlow/timesheetFlowApi";
import {
  billingQuestionIsOpen,
  flattenBillingQuestionsToAnswerRows,
  type BillingAnswerRow,
} from "@/features/timesheetFlow/billingQuestionAnswerRows";
import { BillingAnsweredRowCard } from "@/features/timesheetFlow/components/BillingAnsweredRowCard";
import { BillingAnsweredRowsDialog } from "@/features/timesheetFlow/components/BillingAnsweredRowsDialog";
import { resolveAnsweredArticleDisplay } from "@/features/timesheetFlow/billingAnswerPositionUtils";
import {
  hasUnreadBillingAnswers,
  resolveTimesheetRecordStatus,
  statusBadgeClass,
  timesheetStatusLabel,
} from "@/pages/stundennachweise/timesheetFilterLogic";
import { TimesheetBadge } from "@/pages/stundennachweise/TimesheetBadge";
import {
  runTimesheetGdInvoiceLink,
  runTimesheetHeroInvoiceLink,
} from "@/features/timesheetFlow/timesheetInvoiceHandlers";
import type {
  TimesheetMaterialRecord,
  TimesheetRecord,
  TimesheetRowRecord,
} from "@/features/timesheetFlow/timesheetFlowTypes";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userCanSeePrices } from "@/lib/auth";
import { listBillingMembers, type BillingMember } from "@/lib/timesheets";
import {
  chooseBillingPositionLabel,
  formatBillingPositionReference,
  type DokumentBillingQuestion,
} from "@/lib/dokumentBilling";

const COMPACT_CARD = "!p-2.5";
const COMPACT_TITLE = "!mb-2 text-sm";

function sumHours(rows: TimesheetRowRecord[]): number {
  return rows.reduce((sum, row) => sum + (Number(row.hours_decimal) || 0), 0);
}

function memberLogin(m: BillingMember): string {
  return String(m.user_login ?? m.login ?? m.samAccountName ?? "").trim();
}

function memberLabel(m: BillingMember): string {
  const login = memberLogin(m);
  const name = m.displayName ? String(m.displayName) : "";
  return name ? `${name} (${login})` : login;
}

function questionIsOpen(q: BillingQuestion): boolean {
  return billingQuestionIsOpen(q);
}

function invoiceQuestionAsBilling(q: DokumentBillingQuestion): BillingQuestion {
  return {
    id: q.id,
    question_text: q.question_text,
    response_text: q.response_text,
    responded_at: q.responded_at,
    created_at: q.created_at,
    created_by_login: q.created_by_login,
    items: (q.items || []).map((item) => {
      const live = formatBillingPositionReference({
        displayNumber: item.position_nr,
        bezeichnung: item.material_text,
        beschreibung: item.position_beschreibung,
        menge: item.material_menge,
        einheit: item.material_einheit,
        heroNr: item.hero_nr,
      });
      const line = chooseBillingPositionLabel(live, item.position_label);
      return {
        id: item.id,
        question_text: item.question_text ?? undefined,
        response_text: item.response_text ?? undefined,
        price: item.price,
        material_text: line || item.material_text,
        material_menge: line ? undefined : item.material_menge,
        material_einheit: line ? undefined : item.material_einheit,
        responded_at: item.responded_at,
      };
    }),
  };
}

function formatBillingPrice(value: unknown): string {
  if (value == null || value === "") return "";
  const num = Number(String(value).replace(",", "."));
  if (Number.isFinite(num)) {
    return `${num.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }
  return `${String(value)} €`;
}

function QuestionStatusBadge({ open, compact }: { open: boolean; compact?: boolean }) {
  return (
    <span
      className={clsx(
        "shrink-0 rounded-full font-semibold uppercase tracking-wide",
        compact ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]",
        open
          ? "bg-amber-500/20 text-amber-900 dark:text-amber-100"
          : "bg-status-green/15 text-status-green",
      )}
    >
      {open ? "Wartet auf Antwort" : "Beantwortet"}
    </span>
  );
}

function QaRow({
  label,
  children,
  muted,
}: {
  label: string;
  children: ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="text-xs leading-snug">
      <div className="font-semibold text-label-secondary">{label}</div>
      <div
        className={clsx(
          "mt-0.5 whitespace-pre-wrap",
          muted ? "text-label-tertiary" : "text-label",
        )}
      >
        {children}
      </div>
    </div>
  );
}

function BillingQuestionDetailCard({
  question,
  materials,
  compact,
  sourceLabel,
  onAnswer,
}: {
  question: BillingQuestion;
  materials: TimesheetMaterialRecord[];
  compact?: boolean;
  sourceLabel?: string | null;
  onAnswer?: () => void;
}) {
  const { user } = useCurrentUser();
  const canSeePrices = userCanSeePrices(user);
  const open = questionIsOpen(question);
  const items = Array.isArray(question.items) ? question.items : [];
  const generalQuestion = String(question.question_text || "").trim();
  const generalAnswer = String(question.response_text || "").trim();

  const materialById = useMemo(() => {
    const map = new Map<number, TimesheetMaterialRecord>();
    for (const mat of materials) {
      if (mat.id != null) map.set(mat.id, mat);
    }
    return map;
  }, [materials]);

  return (
    <li
      className={clsx(
        "flex h-full flex-col rounded-ios bg-card-elevated ring-1 ring-separator/40",
        compact ? "p-2.5 text-xs" : "p-3 text-sm",
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className="text-[10px] font-medium tabular-nums text-label-tertiary">#{question.id}</span>
        <QuestionStatusBadge open={open} compact={compact} />
      </div>
      {sourceLabel ? (
        <p className="mb-2 text-[11px] font-medium text-label-secondary">{sourceLabel}</p>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-2">
        {generalQuestion ? (
          <div className="space-y-1 rounded-md bg-fill-tertiary/30 px-2 py-1.5">
            <QaRow label="Frage">{generalQuestion}</QaRow>
            <QaRow label="Antwort" muted={!generalAnswer}>
              {generalAnswer || (open ? "—" : "Keine Antwort")}
            </QaRow>
          </div>
        ) : null}

        {items.map((item) => {
          const mat =
            item.material_position_id != null
              ? materialById.get(Number(item.material_position_id))
              : undefined;
          const matLine = mat
            ? formatBillingMaterialLine(mat).line
            : formatBillingMaterialLine({
                text: item.material_text ?? undefined,
                menge: item.material_menge ?? undefined,
                einheit: item.material_einheit ?? undefined,
              } as TimesheetMaterialRecord).line || null;
          const itemQuestion = String(item.question_text || "").trim();
          const itemAnswer = String(item.response_text || "").trim();
          const price = canSeePrices
            ? formatBillingPrice((item as { price?: unknown }).price)
            : "";

          return (
            <div
              key={item.id}
              className="space-y-1 rounded-md bg-fill-tertiary/30 px-2 py-1.5"
            >
              {matLine ? (
                <p className="text-[10px] font-medium uppercase tracking-wide text-label-tertiary">
                  {matLine}
                </p>
              ) : null}
              {itemQuestion ? <QaRow label="Frage">{itemQuestion}</QaRow> : null}
              <QaRow label="Antwort" muted={!itemAnswer}>
                {itemAnswer || (open ? "—" : "Keine Antwort")}
              </QaRow>
              {price ? <QaRow label="Preis">{price}</QaRow> : null}
            </div>
          );
        })}

        {!generalQuestion && items.length === 0 ? (
          <p className="text-label-tertiary">Keine Fragedetails.</p>
        ) : null}
      </div>
      {open && onAnswer ? (
        <div className="mt-2 flex justify-end">
          <Button type="button" size="sm" onClick={onAnswer}>
            Antworten
          </Button>
        </div>
      ) : null}
    </li>
  );
}

export function TimesheetBillingTab({
  timesheet,
  rows,
  materials,
  permissions,
  onReload,
  onRefresh,
  onError,
  onOpenAnswerDialog,
  onOpenInvoiceAnswerDialog,
  linkedInvoiceQuestions = [],
  linkedInvoiceLabel = "",
}: {
  timesheet: TimesheetRecord;
  rows: TimesheetRowRecord[];
  materials: TimesheetMaterialRecord[];
  permissions: TimesheetPermissions;
  onReload: () => Promise<void>;
  onRefresh: () => void;
  onError: (message: string) => void;
  onOpenAnswerDialog?: () => void;
  /** Öffnet den Antwortdialog für Rückfragen der verknüpften Rechnung. */
  onOpenInvoiceAnswerDialog?: () => void;
  /** Rückfragen der verknüpften Rechnung. */
  linkedInvoiceQuestions?: DokumentBillingQuestion[];
  linkedInvoiceLabel?: string;
}) {
  const timesheetId = timesheet.id;
  const normalizedStatus = resolveTimesheetRecordStatus(timesheet);
  const compactView = permissions.canBilling;
  const showGdInvoiceLink = showGdInvoiceLinkInBillingTab(normalizedStatus, permissions.canBilling);

  const [members, setMembers] = useState<BillingMember[]>([]);
  const [assigneeLogin, setAssigneeLogin] = useState(String(timesheet.billing_assignee_login || ""));
  const [questions, setQuestions] = useState<BillingQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [generalQuestion, setGeneralQuestion] = useState("");
  const [materialQuestions, setMaterialQuestions] = useState<Record<number, string>>({});
  const [heroInvoices, setHeroInvoices] = useState<HeroProjectInvoiceOption[]>([]);
  const [heroInvoicesLoading, setHeroInvoicesLoading] = useState(false);
  const [heroInvoicesError, setHeroInvoicesError] = useState<string | null>(null);
  const [selectedHeroInvoiceId, setSelectedHeroInvoiceId] = useState("");
  const [gdInvoices, setGdInvoices] = useState<GdProjectInvoiceOption[]>([]);
  const [gdInvoicesLoading, setGdInvoicesLoading] = useState(false);
  const [gdInvoicesError, setGdInvoicesError] = useState<string | null>(null);
  const [selectedGdInvoiceId, setSelectedGdInvoiceId] = useState("");
  const [actionLog, setActionLog] = useState<ActionLogEntry[]>([]);
  const [followUpBusyKey, setFollowUpBusyKey] = useState<string | null>(null);
  const [answeredDialogOpen, setAnsweredDialogOpen] = useState(false);

  const totalHours = useMemo(() => {
    if (timesheet.total_hours != null) return Number(timesheet.total_hours);
    return sumHours(rows);
  }, [timesheet.total_hours, rows]);

  const workItems = useMemo(() => {
    const text = String(timesheet.work_description || "").trim();
    return text ? [text] : [];
  }, [timesheet.work_description]);

  const materialLines = useMemo(
    () =>
      materials
        .map((m) => formatBillingMaterialLine(m))
        .filter((m) => m.line),
    [materials],
  );

  const openQuestions = useMemo(() => questions.filter(questionIsOpen), [questions]);
  const answeredQuestions = useMemo(() => questions.filter((q) => !questionIsOpen(q)), [questions]);
  const invoiceBillingQuestions = useMemo(
    () => linkedInvoiceQuestions.map(invoiceQuestionAsBilling),
    [linkedInvoiceQuestions],
  );
  const openInvoiceQuestions = useMemo(
    () => invoiceBillingQuestions.filter(questionIsOpen),
    [invoiceBillingQuestions],
  );
  const answeredInvoiceQuestions = useMemo(
    () => invoiceBillingQuestions.filter((q) => !questionIsOpen(q)),
    [invoiceBillingQuestions],
  );
  const visibleQuestionCount = questions.length + invoiceBillingQuestions.length;

  const loadQuestions = useCallback(async () => {
    setQuestionsLoading(true);
    try {
      const [list, log] = await Promise.all([
        listBillingQuestions(timesheetId, { includeAnswered: true }),
        permissions.canBilling ? getActionLog(timesheetId).catch(() => []) : Promise.resolve([]),
      ]);
      setQuestions(list);
      setActionLog(log);
      setQuestionsError(null);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Rückfragen konnten nicht geladen werden.";
      setQuestionsError(message);
      onError(message);
    } finally {
      setQuestionsLoading(false);
    }
  }, [timesheetId, onError, permissions.canBilling]);

  useEffect(() => {
    void loadQuestions();
  }, [loadQuestions]);

  useEffect(() => {
    if (!permissions.canBilling) return;
    void listBillingMembers()
      .then(setMembers)
      .catch(() => setMembers([]));
  }, [permissions.canBilling]);

  useEffect(() => {
    setAssigneeLogin(String(timesheet.billing_assignee_login || ""));
  }, [timesheet.billing_assignee_login, timesheet.id]);

  useEffect(() => {
    if (!showGdInvoiceLink) {
      setHeroInvoices([]);
      setSelectedHeroInvoiceId("");
      setHeroInvoicesError(null);
      return;
    }
    setHeroInvoicesLoading(true);
    setHeroInvoicesError(null);
    setSelectedHeroInvoiceId("");
    void listHeroProjectInvoices(timesheetId)
      .then((data) => {
        const invoices = data as HeroProjectInvoiceOption[];
        setHeroInvoices(invoices);
        const gdId = Number(timesheet.gd_rechnung_id ?? 0);
        if (Number.isFinite(gdId) && gdId > 0) return;
        const current = String(
          timesheet.invoice_customer_document_id || timesheet.invoice_document_id || "",
        ).trim();
        if (!current) return;
        const match = invoices.find((inv) => {
          const customerId = String(inv.customer_document_id ?? inv.id ?? inv.document_id ?? "");
          const heroId = String(inv.hero_document_id ?? inv.document_id ?? inv.id ?? "");
          return customerId === current || heroId === current;
        });
        if (match) {
          setSelectedHeroInvoiceId(
            String(match.customer_document_id ?? match.id ?? match.document_id ?? ""),
          );
        }
      })
      .catch((err: unknown) => {
        setHeroInvoices([]);
        setHeroInvoicesError(
          err instanceof ApiError
            ? err.message
            : "Hero-Rechnungen konnten nicht geladen werden.",
        );
      })
      .finally(() => setHeroInvoicesLoading(false));
  }, [
    showGdInvoiceLink,
    timesheetId,
    timesheet.gd_rechnung_id,
    timesheet.invoice_customer_document_id,
    timesheet.invoice_document_id,
  ]);

  useEffect(() => {
    if (!showGdInvoiceLink) {
      setGdInvoices([]);
      setSelectedGdInvoiceId("");
      return;
    }
    setGdInvoicesLoading(true);
    setGdInvoicesError(null);
    void listGdProjectInvoices(timesheetId)
      .then((data) => {
        setGdInvoices(data);
        const current = Number(timesheet.gd_rechnung_id ?? 0);
        if (Number.isFinite(current) && current > 0) {
          setSelectedGdInvoiceId(String(current));
        }
      })
      .catch((err: unknown) => {
        setGdInvoices([]);
        setGdInvoicesError(
          err instanceof ApiError
            ? err.message
            : "Rechnungsliste (App) konnte nicht geladen werden.",
        );
      })
      .finally(() => setGdInvoicesLoading(false));
  }, [showGdInvoiceLink, timesheetId, timesheet.gd_rechnung_id]);

  const runBusy = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const handleAssign = () => {
    if (!assigneeLogin.trim()) {
      onError("Bitte einen Bearbeiter auswählen.");
      return;
    }
    void runBusy(async () => {
      await assignBilling(timesheetId, assigneeLogin.trim());
      await onReload();
      onRefresh();
    });
  };

  const handleSubmitQuestions = () => {
    const items = materials
      .map((mat) => {
        const id = mat.id;
        if (id == null) return null;
        const text = (materialQuestions[id] || "").trim();
        if (!text) return null;
        return { material_position_id: id, question_text: text };
      })
      .filter(Boolean) as Array<{ material_position_id: number; question_text: string }>;
    const general = generalQuestion.trim();
    if (!general && items.length === 0) {
      onError("Bitte mindestens eine Rückfrage eingeben.");
      return;
    }
    void runBusy(async () => {
      try {
        const created = await createBillingQuestions(timesheetId, {
          question_text: general || null,
          items,
        });
        setGeneralQuestion("");
        setMaterialQuestions({});
        await onReload();
        await loadQuestions();
        if (created?.id != null) {
          setQuestions((prev) => (prev.some((q) => q.id === created.id) ? prev : [...prev, created]));
        }
        onRefresh();
      } catch (err: unknown) {
        onError(err instanceof Error ? err.message : "Rückfrage konnte nicht gesendet werden.");
      }
    });
  };

  const handleLinkHeroInvoice = () => {
    const inv = heroInvoices.find(
      (item) =>
        String(item.customer_document_id ?? item.id ?? item.document_id ?? "") ===
        selectedHeroInvoiceId,
    );
    if (!inv) {
      onError("Bitte eine Hero-Rechnung auswählen.");
      return;
    }
    void runBusy(async () => {
      await runTimesheetHeroInvoiceLink(timesheetId, inv, {
        onReload,
        onRefresh,
        onError,
      });
    });
  };

  const handleLinkGdInvoice = () => {
    const gdId = Number(selectedGdInvoiceId);
    if (!Number.isFinite(gdId) || gdId <= 0) {
      onError("Bitte eine Rechnung auswählen.");
      return;
    }
    void runBusy(async () => {
      await runTimesheetGdInvoiceLink(timesheetId, gdId, {
        onReload,
        onRefresh,
        onError,
      });
    });
  };

  const openAnswerCount = useMemo(
    () => flattenBillingQuestionsToAnswerRows(openQuestions, materials).filter((row) => row.isOpen).length,
    [openQuestions, materials],
  );
  const showTimesheetAnswer = Boolean(
    permissions.canAnswerBillingQuestions && openAnswerCount > 0 && onOpenAnswerDialog,
  );
  const showInvoiceAnswer = Boolean(
    permissions.canAnswerBillingQuestions &&
      openInvoiceQuestions.length > 0 &&
      onOpenInvoiceAnswerDialog,
  );
  const showAnswerPrompt = showTimesheetAnswer || showInvoiceAnswer;
  const answerPromptCount =
    (showTimesheetAnswer ? openAnswerCount : 0) +
    (showInvoiceAnswer ? openInvoiceQuestions.length : 0);

  const answeredRows = useMemo(() => {
    const own = flattenBillingQuestionsToAnswerRows(answeredQuestions, materials).filter(
      (row) => !row.isOpen,
    );
    const fromInvoice = flattenBillingQuestionsToAnswerRows(answeredInvoiceQuestions, materials)
      .filter((row) => !row.isOpen)
      .map((row) => ({
        ...row,
        key: `inv-${row.key}`,
        sourceLabel: linkedInvoiceLabel || null,
      }));
    return [...own, ...fromInvoice];
  }, [answeredQuestions, answeredInvoiceQuestions, materials, linkedInvoiceLabel]);

  const billingAnswersUnread = useMemo(
    () => hasUnreadBillingAnswers(timesheet as Parameters<typeof hasUnreadBillingAnswers>[0]),
    [timesheet],
  );

  const { user } = useCurrentUser();
  const canSeePrices = userCanSeePrices(user);

  const resolveRowArticleDisplay = useCallback(
    (row: BillingAnswerRow) =>
      resolveAnsweredArticleDisplay({
        materialPositionId: row.materialPositionId,
        answerText: row.answerText,
        respondedAt: row.respondedAt,
        materials,
        actionLog,
        showPrices: canSeePrices,
        formatMaterialLine: (material) =>
          formatBillingMaterialLine(material as TimesheetMaterialRecord).line || null,
      }),
    [actionLog, canSeePrices, materials],
  );

  const handleFollowUp = async (row: BillingAnswerRow, text: string) => {
    const trimmed = text.trim();
    if (!trimmed) {
      onError("Bitte eine Nachfrage eingeben.");
      return;
    }
    setFollowUpBusyKey(row.key);
    try {
      const created =
        row.materialPositionId != null
          ? await createBillingQuestions(timesheetId, {
              items: [{ material_position_id: row.materialPositionId, question_text: trimmed }],
            })
          : await createBillingQuestions(timesheetId, { question_text: trimmed });
      await onReload();
      await loadQuestions();
      if (created?.id != null) {
        setQuestions((prev) => (prev.some((q) => q.id === created.id) ? prev : [...prev, created]));
      }
      onRefresh();
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Nachfrage konnte nicht gesendet werden.");
    } finally {
      setFollowUpBusyKey(null);
    }
  };

  const renderAnsweredCard = (row: BillingAnswerRow, unread?: boolean) => {
    const articleDisplay = resolveRowArticleDisplay(row);
    return (
      <BillingAnsweredRowCard
        key={row.key}
        row={row}
        articleLines={articleDisplay.lines}
        unterpositionCount={articleDisplay.unterpositionCount}
        compact={compactView}
        unread={unread}
        canFollowUp={permissions.canBilling && !row.sourceLabel}
        followUpBusy={followUpBusyKey === row.key}
        onFollowUp={permissions.canBilling ? handleFollowUp : undefined}
      />
    );
  };

  const cardProps = compactView ? { className: COMPACT_CARD, titleClassName: COMPACT_TITLE } : {};

  const workCard = (
    <TimesheetFlowCard title="Ausgeführte Arbeiten" {...cardProps}>
      {workItems.length === 0 ? (
        <p className="text-sm text-label-secondary">Keine Arbeiten dokumentiert.</p>
      ) : (
        <p className={clsx("text-label", compactView ? "text-xs leading-snug" : "text-sm")}>
          {workItems.join(" · ")}
        </p>
      )}
    </TimesheetFlowCard>
  );

  const questionSendCard = permissions.canBilling ? (
    <TimesheetFlowCard title="Rückfrage senden" {...cardProps}>
      {!compactView ? (
        <p className="mb-3 text-xs text-label-secondary">
          Materialpositionen und/oder eine allgemeine Frage – mindestens eines ausfüllen.
        </p>
      ) : null}
      {materials.length > 0 ? (
        <div
          className={clsx(
            "mb-2 gap-2",
            compactView ? "grid grid-cols-1 sm:grid-cols-2" : "space-y-2",
          )}
        >
          {materials.map((mat) => {
            const id = mat.id;
            if (id == null) return null;
            const { line } = formatBillingMaterialLine(mat);
            return (
              <TextareaInput
                key={id}
                label={line || `Material ${id}`}
                rows={compactView ? 2 : 2}
                value={materialQuestions[id] || ""}
                onChange={(e) => setMaterialQuestions((prev) => ({ ...prev, [id]: e.target.value }))}
                placeholder="Rückfrage…"
              />
            );
          })}
        </div>
      ) : compactView ? null : (
        <p className="mb-2 text-sm text-label-secondary">Keine Materialpositionen erfasst.</p>
      )}
      <TextareaInput
        label="Allgemeine Rückfrage"
        rows={compactView ? 2 : 3}
        value={generalQuestion}
        onChange={(e) => setGeneralQuestion(e.target.value)}
        placeholder="Frage zur Abrechnung…"
      />
      <Button
        type="button"
        size="sm"
        className={compactView ? "mt-1.5" : "mt-2"}
        disabled={busy}
        onClick={handleSubmitQuestions}
      >
        Senden
      </Button>
    </TimesheetFlowCard>
  ) : null;

  const questionsCard = (
    <TimesheetFlowCard title={`Rückfragen${visibleQuestionCount ? ` (${visibleQuestionCount})` : ""}`} {...cardProps}>
      {questionsLoading ? (
        <p className="text-sm text-label-secondary">Lädt…</p>
      ) : questionsError ? (
        <p className="text-sm text-status-red">{questionsError}</p>
      ) : visibleQuestionCount === 0 ? (
        <p className="text-sm text-label-secondary">Keine Rückfragen vorhanden.</p>
      ) : (
        <div className="space-y-4">
          {answeredRows.length > 0 ? (
            <button
              type="button"
              onClick={() => setAnsweredDialogOpen(true)}
              className={clsx(
                "flex w-full items-center gap-2 rounded-ios border px-3 py-2.5 text-left text-sm text-label transition hover:bg-fill-tertiary/40",
                billingAnswersUnread
                  ? "border-status-green/30 bg-status-green/[0.07]"
                  : "border-separator bg-card-elevated/40",
              )}
            >
              <span
                className={clsx(
                  "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                  billingAnswersUnread
                    ? "bg-status-green/15 text-status-green"
                    : "bg-fill-tertiary text-label-secondary",
                )}
              >
                ✓
              </span>
              <span className="min-w-0 flex-1">
                <span className="font-medium">
                  {billingAnswersUnread
                    ? answeredRows.length === 1
                      ? "1 neue Antwort"
                      : `${answeredRows.length} neue Antworten`
                    : answeredRows.length === 1
                      ? "1 beantwortete Rückfrage"
                      : `${answeredRows.length} beantwortete Rückfragen`}
                </span>
                <span className="mt-0.5 block text-xs text-label-secondary">
                  Zum Anzeigen klicken
                </span>
              </span>
              <span className="shrink-0 text-xs font-medium text-accent">Anzeigen</span>
            </button>
          ) : null}

          {showAnswerPrompt ? (
            <div className="rounded-ios-lg border border-amber-500/30 bg-amber-500/5 p-4 sm:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-base font-semibold text-label">
                    {answerPromptCount === 1
                      ? "1 offene Rückfrage wartet auf Antwort"
                      : `${answerPromptCount} offene Rückfragen warten auf Antwort`}
                  </p>
                  <p className="mt-1 text-sm text-label-secondary">
                    Im Antwort-Dialog kannst du jede Frage einzeln beantworten — übersichtlich und
                    mit mehr Platz.
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {showTimesheetAnswer ? (
                    <Button type="button" onClick={onOpenAnswerDialog}>
                      Rückfragen beantworten
                    </Button>
                  ) : null}
                  {showInvoiceAnswer ? (
                    <Button type="button" onClick={onOpenInvoiceAnswerDialog}>
                      {showTimesheetAnswer
                        ? "Rechnungs-Rückfragen beantworten"
                        : "Rückfragen beantworten"}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          {openQuestions.length + openInvoiceQuestions.length > 0 ? (
            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-label-secondary">
                Wartet auf Antwort ({openQuestions.length + openInvoiceQuestions.length})
              </h4>
              <ul className={clsx("grid gap-3", compactView ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1 lg:grid-cols-2")}>
                {openQuestions.map((q) => (
                  <BillingQuestionDetailCard
                    key={`ts-${q.id}`}
                    question={q}
                    materials={materials}
                    compact={compactView}
                    onAnswer={
                      permissions.canAnswerBillingQuestions ? onOpenAnswerDialog : undefined
                    }
                  />
                ))}
                {openInvoiceQuestions.map((q) => (
                  <BillingQuestionDetailCard
                    key={`inv-${q.id}`}
                    question={q}
                    materials={materials}
                    compact={compactView}
                    sourceLabel={linkedInvoiceLabel || null}
                    onAnswer={
                      permissions.canAnswerBillingQuestions
                        ? onOpenInvoiceAnswerDialog
                        : undefined
                    }
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </TimesheetFlowCard>
  );

  const assignCard = permissions.canBilling ? (
    <TimesheetFlowCard title="Zuweisung" {...cardProps}>
      <NativeSelectField
        label="Bearbeiter"
        selectSize="sm"
        value={assigneeLogin}
        onChange={(e) => setAssigneeLogin(e.target.value)}
      >
        <option value="">— Auswählen —</option>
        {members.map((m) => {
          const login = memberLogin(m);
          if (!login) return null;
          return (
            <option key={login} value={login}>
              {memberLabel(m)}
            </option>
          );
        })}
        {assigneeLogin && !members.some((m) => memberLogin(m) === assigneeLogin) ? (
          <option value={assigneeLogin}>{assigneeLogin}</option>
        ) : null}
      </NativeSelectField>
      <Button
        type="button"
        size="sm"
        className="mt-1.5 w-full sm:w-auto"
        disabled={busy || !assigneeLogin}
        onClick={handleAssign}
      >
        Zuweisen
      </Button>
    </TimesheetFlowCard>
  ) : null;

  const hasInvoiceLink =
    Boolean(timesheet.gd_rechnung_id) ||
    Boolean(timesheet.invoice_customer_document_id) ||
    Boolean(timesheet.invoice_document_id);
  const linkSelectionPending =
    (Boolean(selectedGdInvoiceId) && gdInvoicesLoading) ||
    (Boolean(selectedHeroInvoiceId) && heroInvoicesLoading);

  const gdLinkCard = showGdInvoiceLink ? (
    <TimesheetFlowCard title="Rechnung verknüpfen" {...cardProps}>
      <p className="mb-2 text-xs text-label-tertiary">
        Bestehende Rechnung aus der App oder eine Hero-Rechnung mit diesem Stundennachweis
        verknüpfen (ohne neue Positionen anzulegen).
      </p>
      {gdInvoicesError ? (
        <p className="mb-2 text-xs text-status-red">{gdInvoicesError}</p>
      ) : null}
      {heroInvoicesError ? (
        <p className="mb-2 text-xs text-status-red">{heroInvoicesError}</p>
      ) : null}
      <NativeSelectField
        label="Rechnung (App)"
        selectSize="sm"
        value={selectedGdInvoiceId}
        onChange={(e) => {
          setSelectedGdInvoiceId(e.target.value);
          if (e.target.value) setSelectedHeroInvoiceId("");
        }}
        disabled={gdInvoicesLoading || gdInvoices.length === 0}
      >
        <option value="">
          {gdInvoicesLoading
            ? "Lädt…"
            : gdInvoices.length === 0
              ? "Keine Rechnungen im Projekt"
              : "— Auswählen —"}
        </option>
        {gdInvoices.map((inv) => (
          <option key={inv.id} value={String(inv.id)}>
            {gdProjectInvoiceOptionLabel(inv)}
          </option>
        ))}
      </NativeSelectField>
      <NativeSelectField
        label="Rechnung (Hero)"
        selectSize="sm"
        className="mt-2"
        value={selectedHeroInvoiceId}
        onChange={(e) => {
          setSelectedHeroInvoiceId(e.target.value);
          if (e.target.value) setSelectedGdInvoiceId("");
        }}
        disabled={heroInvoicesLoading || heroInvoices.length === 0}
      >
        <option value="">
          {heroInvoicesLoading
            ? "Lädt…"
            : heroInvoices.length === 0
              ? "Keine Hero-Rechnungen im Projekt"
              : "— Auswählen —"}
        </option>
        {heroInvoices.map((inv, idx) => {
          const id = String(inv.customer_document_id ?? inv.id ?? inv.document_id ?? idx);
          return (
            <option key={id} value={id}>
              {heroInvoiceOptionLabel(inv)}
            </option>
          );
        })}
      </NativeSelectField>
      <Button
        type="button"
        size="sm"
        className="mt-1.5 w-full sm:w-auto"
        disabled={
          busy ||
          linkSelectionPending ||
          (!selectedGdInvoiceId && !selectedHeroInvoiceId)
        }
        onClick={selectedHeroInvoiceId ? handleLinkHeroInvoice : handleLinkGdInvoice}
      >
        {hasInvoiceLink ? "Neu verknüpfen" : "Verknüpfen"}
      </Button>
    </TimesheetFlowCard>
  ) : null;

  const adminCards = [assignCard, gdLinkCard].filter(Boolean);
  const adminRow =
    permissions.canBilling && adminCards.length > 0 ? (
      <div
        className={clsx(
          "grid gap-2",
          adminCards.length > 1 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1",
        )}
      >
        {assignCard}
        {gdLinkCard}
      </div>
    ) : null;

  const orderId = timesheet.auftrag_id ? Number(timesheet.auftrag_id) : null;

  const billingMain = compactView ? (
    <div className="space-y-2.5">
      {workCard}
      {questionSendCard}
      {questionsCard}
      {adminRow}
    </div>
  ) : (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-label-secondary">
          Stundensumme: <span className="font-semibold text-label">{formatHoursTotal(totalHours)}</span>
        </p>
        {timesheet.billing_assignee_login ? (
          <p className="text-xs text-label-tertiary">
            Zugewiesen: {String(timesheet.billing_assignee_name || timesheet.billing_assignee_login)}
          </p>
        ) : (
          <p className="text-xs text-label-tertiary">Noch nicht zugewiesen</p>
        )}
      </div>

      {workCard}

      <TimesheetFlowCard title={`Material (${materialLines.length})`}>
        {materialLines.length === 0 ? (
          <p className="text-sm text-label-secondary">Keine Materialien erfasst.</p>
        ) : (
          <ul className="space-y-1 text-sm text-label">
            {materialLines.map((m, idx) => (
              <li key={`${m.line}-${idx}`} className="rounded-ios bg-card-elevated/60 px-2 py-1.5">
                {m.line}
              </li>
            ))}
          </ul>
        )}
        {timesheet.material_note ? (
          <p className="mt-2 text-xs text-label-tertiary">Notiz: {String(timesheet.material_note)}</p>
        ) : null}
      </TimesheetFlowCard>

      {questionsCard}
    </div>
  );

  return (
    <>
      <div className="eh-ts-billing-layout min-h-0 w-full">
        <div className="eh-ts-billing-main">{billingMain}</div>
        {orderId && Number.isFinite(orderId) ? (
          <TimesheetBillingNotesPanel orderId={orderId} />
        ) : null}
      </div>

      {answeredDialogOpen ? (
        <BillingAnsweredRowsDialog
          open={answeredDialogOpen}
          onClose={() => setAnsweredDialogOpen(false)}
          count={answeredRows.length}
          statusBadge={
            <TimesheetBadge className={statusBadgeClass(normalizedStatus, user)}>
              {timesheetStatusLabel(normalizedStatus)}
            </TimesheetBadge>
          }
        >
          {answeredRows.map((row) => renderAnsweredCard(row, billingAnswersUnread))}
        </BillingAnsweredRowsDialog>
      ) : null}
    </>
  );
}
